import React, { useMemo, useState } from "react";
import classNames from "classnames";
import styles from "./RiskProfileContent.module.scss";
import Input from "../../components/Input/Input";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import { useDispatch, useSelector } from "react-redux";
import { showModal } from "../../redux/modalStore";
import { useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  CircleMinus,
  Lock,
  Shield,
  ShieldAlert,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const MAIN_CHAPTER_TITLES = {
  A: "General Corporate Info",
  B: "IT & Data Systems",
  C: "Operational Activities",
  D: "Financials & Procurement",
  E: "Customs Compliance & Goods Flow",
  F: "Suppliers & External Services",
  G: "Staff & Training",
  H: "Safety, Security & Access",
};

const LEVEL_ORDER = ["high", "medium", "low", "none"];

const normalizeLevel = (level) => {
  const normalized = String(level || "none").trim().toLowerCase();
  if (LEVEL_ORDER.includes(normalized)) return normalized;
  return "none";
};

const getRiskSearchText = (risk) =>
  [
    risk?.risk_id,
    risk?.risk_num,
    risk?.qms_id,
    risk?.qms_description,
    risk?.description,
    risk?.reason,
    risk?.level,
  ]
    .filter((value) => value !== null && value !== undefined)
    .join(" ")
    .toLowerCase();

const resolveLinkedItemKey = (value) => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (value && typeof value === "object") {
    if (value.key !== undefined && value.key !== null) return String(value.key);
    if (value.id !== undefined && value.id !== null) return String(value.id);
  }
  return "";
};

const RiskProfileContent = ({
  title,
  risksData,
  findingsData,
  improvementsData,
  auditsData,
}) => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const currentUser = useSelector((state) => state.user.user);

  const canEdit = useMemo(() => {
    const role = currentUser?.Role?.toLowerCase?.();
    return role === "consultant" || role === "qmsadmin";
  }, [currentUser]);

  const [searchQuery, setSearchQuery] = useState("");
  const normalizedSearch = String(searchQuery).trim().toLowerCase();

  const riskItems = useMemo(() => Object.values(risksData || {}), [risksData]);

  const kpis = useMemo(() => {
    const counts = { total: 0, high: 0, medium: 0, low: 0, none: 0 };

    riskItems.forEach((risk) => {
      counts.total += 1;
      const level = normalizeLevel(risk?.level);
      counts[level] += 1;
    });

    return counts;
  }, [riskItems]);

  const findingByKey = useMemo(() => {
    const map = new Map();
    Object.values(findingsData || {}).forEach((finding) => {
      if (finding?.key !== undefined && finding?.key !== null) {
        map.set(String(finding.key), finding);
      }
      if (finding?.id !== undefined && finding?.id !== null) {
        map.set(String(finding.id), finding);
      }
    });
    return map;
  }, [findingsData]);

  const linkedCountsByRiskId = useMemo(() => {
    const findingsByRiskId = {};
    const improvementsByRiskId = {};
    const auditsByRiskId = {};

    Object.values(findingsData || {})
      .filter((finding) => !finding?.archived)
      .forEach((finding) => {
        const riskId = finding?.risk?.risk_id;
        if (!riskId) return;
        const key = String(riskId);
        findingsByRiskId[key] = (findingsByRiskId[key] || 0) + 1;
      });

    Object.values(improvementsData || {})
      .filter((project) => !project?.archived)
      .forEach((project) => {
        const linkedFindingKeys = Array.isArray(project?.linked_findings)
          ? project.linked_findings
          : [];

        const riskIds = new Set();
        linkedFindingKeys.forEach((findingRef) => {
          const key = resolveLinkedItemKey(findingRef);
          if (!key) return;
          const finding =
            findingByKey.get(key) ||
            findingsData?.[key] ||
            findingsData?.[Number.isNaN(Number(key)) ? key : Number(key)];
          const riskId = finding?.risk?.risk_id;
          if (!riskId) return;
          riskIds.add(String(riskId));
        });

        riskIds.forEach((riskId) => {
          improvementsByRiskId[riskId] = (improvementsByRiskId[riskId] || 0) + 1;
        });
      });

    Object.values(auditsData || {})
      .filter((audit) => !audit?.archived)
      .forEach((audit) => {
        const linkedFindingKeys = Array.isArray(audit?.linked_findings)
          ? audit.linked_findings
          : [];

        const riskIds = new Set();
        linkedFindingKeys.forEach((findingRef) => {
          const key = resolveLinkedItemKey(findingRef);
          if (!key) return;
          const finding =
            findingByKey.get(key) ||
            findingsData?.[key] ||
            findingsData?.[Number.isNaN(Number(key)) ? key : Number(key)];
          const riskId = finding?.risk?.risk_id;
          if (!riskId) return;
          riskIds.add(String(riskId));
        });

        riskIds.forEach((riskId) => {
          auditsByRiskId[riskId] = (auditsByRiskId[riskId] || 0) + 1;
        });
      });

    return { findingsByRiskId, improvementsByRiskId, auditsByRiskId };
  }, [auditsData, findingByKey, findingsData, improvementsData]);

  const filteredRisks = useMemo(() => {
    if (!normalizedSearch) return riskItems;
    return riskItems.filter((risk) =>
      getRiskSearchText(risk).includes(normalizedSearch),
    );
  }, [normalizedSearch, riskItems]);

  const groupedByLetterThenChapter = useMemo(() => {
    const grouped = {};

    filteredRisks.forEach((risk) => {
      const qmsId = String(risk?.qms_id || "").trim().toUpperCase();
      const letter = qmsId ? qmsId.charAt(0) : "Other";
      const chapterKey = qmsId || "Unclassified";

      if (!grouped[letter]) {
        grouped[letter] = {
          letter,
          title: MAIN_CHAPTER_TITLES[letter] || "Chapter",
          chapters: {},
        };
      }

      if (!grouped[letter].chapters[chapterKey]) {
        grouped[letter].chapters[chapterKey] = {
          qms_id: chapterKey,
          qms_description: risk?.qms_description || "",
          risks: [],
        };
      }

      grouped[letter].chapters[chapterKey].risks.push(risk);
    });

    return grouped;
  }, [filteredRisks]);

  const sortedLetters = useMemo(
    () => Object.keys(groupedByLetterThenChapter).sort(),
    [groupedByLetterThenChapter],
  );

  const [openLetters, setOpenLetters] = useState([]);

  const toggleAllLetters = () => {
    const areAllOpen = sortedLetters.every((letter) => openLetters.includes(letter));
    setOpenLetters(areAllOpen ? [] : sortedLetters);
  };

  const toggleLetter = (letter) => {
    setOpenLetters((prev) =>
      prev.includes(letter) ? prev.filter((l) => l !== letter) : [...prev, letter],
    );
  };

  const openEditModal = (riskNum) => {
    dispatch(
      showModal({
        name: "risk_profile_edit_modal",
        data: { riskNum },
      }),
    );
  };

  const kpiCards = useMemo(
    () => [
      {
        key: "total",
        label: t("riskProfile.kpis.total"),
        value: kpis.total,
        icon: <Activity size={18} />,
        accentClass: styles.kpiIconNeutral,
      },
      {
        key: "high",
        label: t("riskProfile.kpis.high"),
        value: kpis.high,
        icon: <ShieldAlert size={18} />,
        accentClass: styles.kpiIconWarning,
      },
      {
        key: "medium",
        label: t("riskProfile.kpis.medium"),
        value: kpis.medium,
        icon: <AlertTriangle size={18} />,
        accentClass: styles.kpiIconInfo,
      },
      {
        key: "low",
        label: t("riskProfile.kpis.low"),
        value: kpis.low,
        icon: <Shield size={18} />,
        accentClass: styles.kpiIconActive,
      },
      {
        key: "none",
        label: t("riskProfile.kpis.none"),
        value: kpis.none,
        icon: <CircleMinus size={18} />,
        accentClass: styles.kpiIconMuted,
      },
    ],
    [kpis, t],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.titleRow}>
            <Shield className={styles.titleIcon} />
            <h1 className={styles.title}>{title}</h1>
          </div>
          <div className={styles.subtitle}>{t("riskProfile.subtitle")}</div>
        </div>

        <div className={styles.headerRight}>
          {!canEdit && (
            <div className={styles.viewOnlyBadge}>
              <Lock size={16} />
              <span>{t("riskProfile.viewOnly")}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.kpiGrid}>
        {kpiCards.map((card) => (
          <div key={card.key} className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <span className={styles.kpiLabel}>{card.label}</span>
              <span
                className={classNames(styles.kpiIconBadge, card.accentClass)}
              >
                {card.icon}
              </span>
            </div>
            <div className={styles.kpiValue}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* <div className={styles.controlsRow}>
        <div className={styles.leftControls}>
          <button
            type="button"
            className={styles.toggleAll}
            onClick={toggleAllLetters}
          >
            <ChevronDown
              className={classNames(styles.chevron, {
                [styles.chevronOpen]: openLetters.length === sortedLetters.length,
              })}
            />
            <span>{t("tableAccordion.all")}</span>
          </button>
          <Input
            type="text"
            placeholder={t("tableAccordion.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
            icon={SearchIcon}
            isClearable
            onClear={() => setSearchQuery("")}
          />
        </div>
        <div className={styles.permissionLabel}>
          {currentUser?.Email}{" "}
          {t(
            canEdit
              ? "tableAccordion.permissions.canEdit"
              : "tableAccordion.permissions.canView",
          )}
        </div>
      </div> */}

      <div className={styles.accordion}>
        {sortedLetters.map((letter) => {
          const data = groupedByLetterThenChapter[letter];
          const chapterValues = Object.values(data.chapters);
          const totalLetterRisks = chapterValues.reduce(
            (sum, chapter) => sum + chapter.risks.length,
            0,
          );

          const sortedChapters = chapterValues.sort((a, b) =>
            String(a.qms_id).localeCompare(String(b.qms_id), undefined, {
              numeric: true,
              sensitivity: "base",
            }),
          );

          const isOpen = openLetters.includes(letter);

          return (
            <div key={letter} className={styles.letterGroup}>
              <button
                type="button"
                className={styles.letterHeader}
                onClick={() => toggleLetter(letter)}
              >
                <div className={styles.letterHeaderLeft}>
                  <div className={styles.letterBadge}>{letter}</div>
                  <div className={styles.letterInfo}>
                    <div className={styles.letterTitle}>
                      {t("tableAccordion.chapter")} {letter} — {data.title}
                    </div>
                    <div className={styles.letterSubtitle}>
                      {sortedChapters.length} sub-chapters
                    </div>
                  </div>
                </div>
                <div className={styles.letterHeaderRight}>
                  <div className={styles.mainCountBadge}>
                    {totalLetterRisks} {totalLetterRisks === 1 ? "risk" : "risks"}
                  </div>
                  <ChevronDown
                    className={classNames(styles.chevron, {
                      [styles.chevronOpen]: isOpen,
                    })}
                  />
                </div>
              </button>

              {isOpen && (
                <div className={styles.letterBody}>
                  {sortedChapters.map((chapter) => {
                    const totalRisks = chapter.risks.length;

                    const risksByLevel = chapter.risks.reduce((acc, risk) => {
                      const level = normalizeLevel(risk?.level);
                      acc[level] = acc[level] || [];
                      acc[level].push(risk);
                      return acc;
                    }, {});

                    return (
                      <details
                        key={chapter.qms_id}
                        className={styles.chapterDetails}
                      >
                        <summary className={styles.chapterSummary}>
                          <div className={styles.chapterSummaryLeft}>
                            <ChevronDown className={styles.chevron} />
                            <div className={styles.chapterCode}>
                              {chapter.qms_id}
                            </div>
                            <div className={styles.chapterName}>
                              {chapter.qms_description || t("tableAccordion.noChapterInfo")}
                            </div>
                          </div>
                          <div className={styles.subCountBadge}>
                            {totalRisks} {totalRisks === 1 ? "risk" : "risks"}
                          </div>
                        </summary>

                        <div className={styles.chapterBody}>
                          {LEVEL_ORDER.map((level) => {
                            const levelRisks = risksByLevel[level] || [];
                            if (!levelRisks.length) return null;

                            return (
                              <div key={level} className={styles.levelGroup}>
                                <div className={styles.levelHeader}>
                                  <span
                                    className={classNames(
                                      styles.levelDot,
                                      styles[`dot_${level}`],
                                    )}
                                  />
                                  <span className={styles.levelLabel}>
                                    {t(`tableAccordion.level.${level}`)}{" "}
                                    {t("riskProfile.risksLabel")}
                                  </span>
                                  <span className={styles.levelCountBadge}>
                                    {levelRisks.length}
                                  </span>
                                </div>

                                <div className={styles.riskList}>
                                  {levelRisks.map((risk) => {
                                    const riskId = risk?.risk_id || "";
                                    const chapterCode = risk?.qms_id
                                      ? String(risk.qms_id)
                                      : "";
                                    const chapterName = risk?.qms_description
                                      ? String(risk.qms_description)
                                      : "";
                                    const displayTitle =
                                      chapterCode && chapterName
                                        ? `${chapterCode} - ${chapterName}`
                                        : chapterName ||
                                          risk?.description ||
                                          riskId ||
                                          "Risk";
                                    const findingsCount =
                                      linkedCountsByRiskId.findingsByRiskId[
                                        String(riskId)
                                      ] || 0;
                                    const improvementsCount =
                                      linkedCountsByRiskId.improvementsByRiskId[
                                        String(riskId)
                                      ] || 0;
                                    const auditsCount =
                                      linkedCountsByRiskId.auditsByRiskId[
                                        String(riskId)
                                      ] || 0;

                                    return (
                                      <div
                                        key={risk?.risk_num ?? riskId}
                                        className={classNames(
                                          styles.riskCard,
                                          styles[`risk_${normalizeLevel(risk?.level)}`],
                                        )}
                                      >
                                        <div className={styles.riskHeader}>
                                          <div className={styles.riskHeaderLeft}>
                                            <div className={styles.riskTitle}>
                                              {displayTitle}
                                            </div>
                                            <div className={styles.riskMeta}>
                                              {riskId && !chapterCode && (
                                                <span className={styles.riskId}>
                                                  {riskId}
                                                </span>
                                              )}
                                              {riskId && chapterCode && (
                                                <span className={styles.riskId}>
                                                  {riskId}
                                                </span>
                                              )}
                                            </div>
                                          </div>

                                          <div className={styles.riskHeaderRight}>
                                            <span
                                              className={classNames(
                                                styles.levelPill,
                                                styles[`pill_${normalizeLevel(risk?.level)}`],
                                              )}
                                            >
                                              {t(
                                                `tableAccordion.level.${normalizeLevel(risk?.level)}`,
                                              )}
                                            </span>
                                            {canEdit &&
                                              risk?.risk_num !== undefined &&
                                              risk?.risk_num !== null && (
                                              <button
                                                type="button"
                                                className={styles.editButton}
                                                onClick={() => openEditModal(risk?.risk_num)}
                                              >
                                                {t("riskProfile.edit")}
                                              </button>
                                            )}
                                          </div>
                                        </div>

                                        <details className={styles.riskDetails}>
                                          <summary className={styles.riskDetailsSummary}>
                                            <ChevronDown className={styles.chevron} />
                                            <span>
                                              {t("riskProfile.viewDetails")}
                                            </span>
                                            <span className={styles.detailsMeta}>
                                              {risk?.date
                                                ? t("riskProfile.updated") +
                                                  ": " +
                                                  new Date(risk.date).toLocaleDateString()
                                                : t("tableAccordion.notDefined")}
                                            </span>
                                          </summary>

                                          <div className={styles.riskDetailsBody}>
                                            <div className={styles.linkGrid}>
                                              <button
                                                type="button"
                                                className={styles.linkCard}
                                                onClick={() =>
                                                  navigate(
                                                    `/${orgId}/audits`,
                                                  )
                                                }
                                              >
                                                <div className={styles.linkValue}>
                                                  {auditsCount}
                                                </div>
                                                <div className={styles.linkLabel}>
                                                  {t("riskProfile.links.audits")}
                                                </div>
                                              </button>
                                              <button
                                                type="button"
                                                className={styles.linkCard}
                                                onClick={() =>
                                                  navigate(
                                                    riskId
                                                      ? `/${orgId}/findings?risk=${encodeURIComponent(
                                                          String(riskId),
                                                        )}`
                                                      : `/${orgId}/findings`,
                                                  )
                                                }
                                              >
                                                <div className={styles.linkValue}>
                                                  {findingsCount}
                                                </div>
                                                <div className={styles.linkLabel}>
                                                  {t("riskProfile.links.findings")}
                                                </div>
                                              </button>
                                              <button
                                                type="button"
                                                className={styles.linkCard}
                                                onClick={() =>
                                                  navigate(
                                                    riskId
                                                      ? `/${orgId}/improvement-projects?risk=${encodeURIComponent(
                                                          String(riskId),
                                                        )}`
                                                      : `/${orgId}/improvement-projects`,
                                                  )
                                                }
                                              >
                                                <div className={styles.linkValue}>
                                                  {improvementsCount}
                                                </div>
                                                <div className={styles.linkLabel}>
                                                  {t("riskProfile.links.projects")}
                                                </div>
                                              </button>
                                            </div>

                                            <div className={styles.reasonBlock}>
                                              <div className={styles.reasonTitle}>
                                                {t("tableAccordion.table.reason")}
                                              </div>
                                              <div className={styles.reasonText}>
                                                {risk?.reason
                                                  ? risk.reason
                                                  : t("riskProfile.noReason")}
                                              </div>
                                            </div>
                                          </div>
                                        </details>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {!sortedLetters.length && (
          <div className={styles.emptyState}>
            <div className={styles.emptyTitle}>
              <Shield className={styles.emptyIcon} />
              <div>{t("riskProfile.emptyTitle")}</div>
            </div>
            <div className={styles.emptySubtitle}>
              {normalizedSearch
                ? t("riskProfile.emptyFiltered")
                : t("riskProfile.empty")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskProfileContent;
